// Normalize the HârnMaster Price Cyclopedia JSON into a flat, usable structure.
// Source: Design docs/hmk_price_cyclopedia-2/hmk_price_cyclopedia.json
import rawCyclopedia from '../data/hmk_price_cyclopedia.json'

export interface ShopItem {
  key: string               // 'chandler.brush'
  establishment: string     // 'chandler'
  subsection: string | null // 'drinks', 'instruments', etc. or null for top-level items
  name: string
  weight_lbs: number | null
  value_denarii: number | null
  value_display: string     // '3d', '6–11£', '12d/gl'
  notes: string | null
  is_service: boolean
}

export interface Establishment {
  key: string
  label: string             // 'Chandler'
  category: string          // 'Lighting & Supplies'
  items: ShopItem[]
}

// ── Currency ──────────────────────────────────────────────────────────────────

const UNIT_TO_DENARII: Record<string, number> = {
  d: 1, s: 12, '£': 240, g: 320, 'ƒ': 0.25,
}

function normalizeCost(cost: unknown, unit: unknown): number | null {
  if (cost == null || typeof cost !== 'number') return null
  const factor = typeof unit === 'string' ? (UNIT_TO_DENARII[unit] ?? null) : null
  if (factor == null) return null
  return Math.round(cost * factor * 100) / 100
}

function formatCost(item: Record<string, unknown>): string {
  const unit = item.costUnit as string | undefined
  const per = item.costPer as string | undefined
  const suffix = per ? `/${per}` : ''

  if (item.cost_min != null && item.cost_max != null) {
    return `${item.cost_min}–${item.cost_max}${unit ?? ''}${suffix}`
  }
  if (item.cost_min != null) {
    return `${item.cost_min}${unit ?? ''}+${suffix}`
  }
  if (item.cost != null) {
    return `${item.cost}${unit ?? ''}${suffix}`
  }
  return '—'
}

// ── Weight ────────────────────────────────────────────────────────────────────

function normalizeWeight(weight: unknown, unit: unknown): number | null {
  if (weight == null || typeof weight !== 'number') return null
  const u = typeof unit === 'string' ? unit.toLowerCase() : 'lb'
  if (u === 'lb') return weight
  if (u === 'oz') return Math.round((weight / 16) * 1000) / 1000
  if (u === 'dr') return Math.round((weight / 256) * 1000) / 1000
  return null
}

// ── Label ─────────────────────────────────────────────────────────────────────

function toLabel(snakeKey: string): string {
  return snakeKey.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// ── Item parsing ──────────────────────────────────────────────────────────────

function parseItem(
  estKey: string,
  itemKey: string,
  item: Record<string, unknown>,
  subsection: string | null,
): ShopItem {
  const name = (
    item.item ?? item.service ?? item.herb ?? item.material ?? item.potion_type
  ) as string | undefined ?? toLabel(itemKey)

  return {
    key: `${estKey}.${subsection ? subsection + '.' : ''}${itemKey}`,
    establishment: estKey,
    subsection,
    name: String(name),
    weight_lbs: normalizeWeight(item.weight, item.weightUnit),
    value_denarii: normalizeCost(item.cost ?? item.cost_min, item.costUnit),
    value_display: formatCost(item),
    notes: typeof item.notes === 'string' ? item.notes : null,
    is_service: 'service' in item,
  }
}

// Heuristic: is a given value an "items dict" (object of item records)?
function isItemsDict(val: unknown): val is Record<string, Record<string, unknown>> {
  if (val == null || typeof val !== 'object' || Array.isArray(val)) return false
  // An items dict's values should be objects too (not strings/numbers)
  const entries = Object.values(val as object)
  return entries.length === 0 || typeof entries[0] === 'object'
}

// ── Main export ───────────────────────────────────────────────────────────────

let _cached: Establishment[] | null = null

export function loadCyclopedia(): Establishment[] {
  if (_cached) return _cached

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = rawCyclopedia as any
  const establishments: Establishment[] = []

  for (const estKey of Object.keys(raw.items)) {
    const est = raw.items[estKey] as Record<string, unknown>
    const category = typeof est.category === 'string' ? est.category : ''
    const items: ShopItem[] = []

    for (const sectionKey of Object.keys(est)) {
      if (sectionKey === 'category') continue
      const section = est[sectionKey]
      if (!isItemsDict(section)) continue

      const isTopLevel = sectionKey === 'items'
      const subsection = isTopLevel ? null : sectionKey

      for (const [itemKey, itemVal] of Object.entries(section)) {
        if (typeof itemVal !== 'object' || itemVal === null) continue
        items.push(parseItem(estKey, itemKey, itemVal as Record<string, unknown>, subsection))
      }
    }

    if (items.length === 0) continue

    establishments.push({
      key: estKey,
      label: toLabel(estKey),
      category,
      items,
    })
  }

  _cached = establishments
  return establishments
}

/** Flat list of all items across all establishments — for search. */
export function allItems(): ShopItem[] {
  return loadCyclopedia().flatMap(e => e.items)
}
